import { FC, useMemo, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import { dateFormat } from 'constants/formats';
import { Partner } from 'types';
import { State } from 'redux/initialState';

import Page from 'modules/_shared/components/Pages/Page';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import PageListHeader from 'modules/_shared/components/Headers/PageListHeader';
import Button from 'modules/_shared/components/Buttons';
import GoBackButton from 'modules/_shared/components/Buttons/GoBackButton';
import Breadcrumb, {
  BreadcrumbItem,
} from 'modules/_shared/components/Breadcrumb';

import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import menuTypes from 'constants/menuTypes';

import {
  TradingOfferOperation,
  TradingOfferOperationStatus,
} from 'types/TradingOfferOperation';
import { useEquityMarket } from '../contexts/EquityMarketContext';
import { statusBadgeMap } from '../constants/tradingOfferOperationStatus';
import getOperationReference from '../utils/getOperationReference';

import '../styles/Trading.scss';

const ALL_FILTER = '';

type OperationTypeFilter = '' | 'BUY' | 'SELL';

const operationTypeFilterOptions: {
  value: OperationTypeFilter;
  label: string;
}[] = [
  { value: '', label: 'All' },
  { value: 'BUY', label: 'Purchase' },
  { value: 'SELL', label: 'Sale' },
];

const statusFilterOptions: {
  value: string;
  label: string;
}[] = [
  { value: ALL_FILTER, label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'KYC_REVIEW', label: 'KYCReview' },
  { value: 'VALIDATION', label: 'PendingValidation' },
  { value: 'PAYMENT_PENDING', label: 'PaymentPending' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const getPartyId = (party: string | Partial<Partner>): string | null => {
  if (!party) return null;
  if (typeof party === 'string') return party;
  return (party as Partner)?._id ?? null;
};

const getPartyName = (party: string | Partial<Partner>): string => {
  if (typeof party === 'string') return '-';
  return party?.name || '-';
};

const getOperationType = (
  operation: TradingOfferOperation,
  currentPartnerId: string
): 'BUY' | 'SELL' => {
  const buyerId = getPartyId(operation.buyer);
  return buyerId === currentPartnerId ? 'BUY' : 'SELL';
};

type TranslateFn = ReturnType<typeof useTranslate>['t'];

const getCounterparty = (
  operation: TradingOfferOperation,
  currentPartnerId: string,
  t: TranslateFn
): string => {
  const isBuyer = getOperationType(operation, currentPartnerId) === 'BUY';
  const counterparty = isBuyer ? operation.seller : operation.buyer;
  const role = isBuyer ? t('Seller') : t('Buyer');
  return `${role}: ${getPartyName(counterparty)}`;
};

const getTradingOfferId = (
  tradingOffer: string | Record<string, unknown>
): string => {
  if (!tradingOffer) return '';
  if (typeof tradingOffer === 'string') return tradingOffer;
  return String(tradingOffer?._id ?? '');
};

const isPartnerOperation = (
  operation: TradingOfferOperation,
  currentPartnerId: string
): boolean => {
  const buyerId = getPartyId(operation.buyer);
  const sellerId = getPartyId(operation.seller);
  return buyerId === currentPartnerId || sellerId === currentPartnerId;
};

const TradingOffersMyOperations: FC = () => {
  const { t } = useTranslate();
  const history = useHistory();
  const location = useLocation();
  const { data } = useEquityMarket();

  const queryParams = new URLSearchParams(location.search);
  const partnerId = queryParams.get('partnerId');
  const partnerQuery = partnerId ? `?partnerId=${partnerId}` : '';

  const allOperations = useSelector(
    ({ tradingOfferOperations }: State) =>
      (tradingOfferOperations ?? []) as TradingOfferOperation[]
  );

  useGetMenu(menuTypes.NULL);

  const [typeFilter, setTypeFilter] = useState<OperationTypeFilter>(ALL_FILTER);
  const [statusFilter, setStatusFilter] = useState(ALL_FILTER);

  const partnerOperations = useMemo(() => {
    if (!partnerId) return allOperations;
    return allOperations.filter((op) => isPartnerOperation(op, partnerId));
  }, [allOperations, partnerId]);

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('Dashboard'), to: '/' },
      { label: data.societyName, to: `/sociedad/${data.societyId}` },
      {
        label: t('EquityMarket'),
        to: `/mercado-de-acciones/${data.societyId}${partnerQuery}`,
      },
      { label: t('MyOperations'), isActive: true },
    ],
    [t, data.societyName, data.societyId, partnerQuery]
  );

  const filteredOperations = useMemo(() => {
    let result = partnerOperations;

    if (typeFilter && partnerId) {
      result = result.filter(
        (op) => getOperationType(op, partnerId) === typeFilter
      );
    }

    if (statusFilter) {
      result = result.filter((op) => op.status === statusFilter);
    }

    return result;
  }, [partnerOperations, typeFilter, statusFilter, partnerId]);

  const handleGoToOperationDetail = useCallback(
    (operationId: string) => {
      history.push(
        `/detalle-operacion-oferta/${data.societyId}/${operationId}${partnerQuery}`
      );
    },
    [history, data.societyId, partnerQuery]
  );

  const handleGoToMarket = useCallback(() => {
    history.push(`/mercado-de-acciones/${data.societyId}${partnerQuery}`);
  }, [history, data.societyId, partnerQuery]);

  const pageActions = [
    <GoBackButton key="go-back" icon="ni-arrow-left" />,
    <Button key="go-to-market" onClick={handleGoToMarket}>
      <span>{t('GoToMarket')}</span>
    </Button>,
  ];

  const getTypeBadge = (type: 'BUY' | 'SELL') => {
    if (type === 'BUY') {
      return (
        <div className="d-flex align-items-center gap-2">
          <div className="icon-circle bg-info-dim text-info">
            <em className="icon ni ni-arrow-down-circle" />
          </div>
          <span className="badge badge-dim bg-info">{t('Purchase')}</span>
        </div>
      );
    }
    return (
      <div className="d-flex align-items-center gap-2">
        <div className="icon-circle bg-success-dim text-success">
          <em className="icon ni ni-arrow-up-circle" />
        </div>
        <span className="badge badge-dim bg-success">{t('Sale')}</span>
      </div>
    );
  };

  const isPendingForUser = (op: TradingOfferOperation): boolean => {
    const pendingStatuses: TradingOfferOperationStatus[] = [
      'KYC_REVIEW',
      'PAYMENT_PENDING',
    ];
    return pendingStatuses.includes(op.status);
  };

  if (data.isLoading) return <CustomLoading />;

  return (
    <Page id="trading-my-operations-page" className="pb-5">
      <div className="nk-block-head-content mb-3">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <PageListHeader
        subTitle={t('MyOperations')}
        description={t('MyOperationsListDescription')}
        actions={pageActions}
      />

      <div className="nk-block nk-block-lg">
        <div className="card card-bordered">
          <div className="card-inner">
            <h6 className="card-title mb-3">
              <em className="icon ni ni-list text-primary mr-2" />
              {t('OperationsList')}
            </h6>

            <p className="text-soft fs-12px mb-3">
              <em className="icon ni ni-info text-soft mr-1" />
              {t('MyOperationsListHint')}
            </p>

            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0 text-soft small">
                  {t('OperationType')}:
                </label>
                <select
                  className="custom-select custom-select-sm"
                  style={{ minWidth: 140 }}
                  value={typeFilter}
                  onChange={(e) =>
                    setTypeFilter(e.target.value as OperationTypeFilter)
                  }
                >
                  {operationTypeFilterOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.label)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0 text-soft small">
                  {t('Status')}:
                </label>
                <select
                  className="custom-select custom-select-sm"
                  style={{ minWidth: 180 }}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {statusFilterOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.label)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="card-inner p-0">
            {filteredOperations.length === 0 ? (
              <div className="text-center py-4">
                <em className="icon ni ni-inbox fs-3 text-soft d-block mb-2" />
                <p className="text-soft">{t('NoOperationsAvailable')}</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-tranx table-hover mb-0">
                  <thead>
                    <tr className="tb-tnx-head">
                      <th className="tb-tnx-id">
                        <span>{t('OperationType')}</span>
                      </th>
                      <th className="tb-tnx-id">
                        <span>{t('OfferRef')}</span>
                      </th>
                      <th className="tb-tnx-id">
                        <span>{t('Ref')}</span>
                      </th>
                      <th className="tb-tnx-info">
                        <span>{t('Date')}</span>
                      </th>
                      <th className="tb-tnx-info">
                        <span>{t('Counterparty')}</span>
                      </th>
                      <th className="tb-tnx-info">
                        <span>{t('Shares')}</span>
                      </th>
                      <th className="tb-tnx-amount">
                        <span>{t('Amount')}</span>
                      </th>
                      <th className="tb-tnx-id">
                        <span>{t('Status')}</span>
                      </th>
                      <th className="tb-tnx-id text-right" />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOperations.map((op) => {
                      const opType = partnerId
                        ? getOperationType(op, partnerId)
                        : 'BUY';
                      const badge =
                        statusBadgeMap[op.status] || statusBadgeMap.PENDING;
                      const pendingForUser = isPendingForUser(op);

                      return (
                        <tr
                          key={op._id}
                          className={`tb-tnx-item${pendingForUser ? ' bg-warning-dim' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleGoToOperationDetail(op._id)}
                        >
                          <td className="tb-tnx-id">{getTypeBadge(opType)}</td>
                          <td className="tb-tnx-id">
                            <span className="text-soft">
                              {op.tradingOffer
                                ? `${opType === 'SELL' ? 'V' : 'C'}-${getTradingOfferId(op.tradingOffer as string | Record<string, unknown>).slice(-3)}`
                                : '—'}
                            </span>
                          </td>
                          <td className="tb-tnx-id">
                            <span className="text-soft">
                              {getOperationReference(op)}
                            </span>
                          </td>
                          <td className="tb-tnx-info">
                            <span>
                              {format(new Date(op.requestDate), dateFormat)}
                            </span>
                          </td>
                          <td className="tb-tnx-info">
                            <span>
                              {partnerId
                                ? getCounterparty(op, partnerId, t)
                                : '-'}
                            </span>
                          </td>
                          <td className="tb-tnx-info">
                            <span>
                              {formatNumber(op.sharesCount)}
                              {op.shareClass ? ` · ${op.shareClass}` : ''}
                            </span>
                          </td>
                          <td className="tb-tnx-amount">
                            <strong>
                              {formatCurrencyDecimals(op.totalPrice)}
                            </strong>
                          </td>
                          <td className="tb-tnx-id">
                            <span
                              className={`badge badge-dim ${badge.className}`}
                            >
                              <em className={`icon ni ${badge.icon} mr-1`} />
                              {t(badge.label)}
                            </span>
                            {pendingForUser && (
                              <span className="badge badge-warning ml-1 mt-1 d-inline-block">
                                {t('PendingForYou')}
                              </span>
                            )}
                          </td>
                          <td className="tb-tnx-id text-right">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGoToOperationDetail(op._id);
                              }}
                            >
                              {t('ViewDetail')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card-inner bg-lighter border-top">
            <button
              type="button"
              className="btn btn-outline-primary mr-2"
              onClick={() =>
                history.push(
                  `/mercado-de-acciones/${data.societyId}${partnerQuery}`
                )
              }
            >
              {t('MyOperationsSummary')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleGoToMarket}
            >
              {t('GoToMarket')}
            </button>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default TradingOffersMyOperations;
